import { Box } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import {
  leadUrgency,
  type SalesOrgLeadCard,
} from "@xop/dashboard-shared";

/** The minimal lead shape the urgency overlay needs. */
export type UrgencyLead = Pick<
  SalesOrgLeadCard,
  "status" | "deadline" | "rfp_deadline" | "deadline_verified"
>;

/**
 * The "super hot mode" pill — the operator's answer to "this lead's window is
 * closing, contact it NOW". Amber for the ≤14-day `super_hot` tier, a gently
 * pulsing red for the ≤3-day `last_chance` tier. Renders nothing for a lead
 * with no live, near deadline (tier `none`).
 *
 * The pill shows ONLY the action ("Act within 12 days" / "Due today") — the
 * tier word itself ("SUPER HOT" / "LAST CHANCE") lives in the lead's heat pill
 * (`leadHeatDisplay`), so the two surfaces split the old "Super hot · act within
 * 12 days" caption rather than repeating it. The tier still drives the badge
 * colour, icon, and hover tooltip.
 *
 * Drives off the shared, unit-tested {@link leadUrgency} so it can never drift
 * from the sort, the pulse card, or the alert. Palette tokens only (no raw hex).
 * `now` is injectable for deterministic tests.
 */
export function UrgencyBadge({
  lead,
  now,
  size = "md",
}: {
  lead: UrgencyLead;
  now?: Date;
  size?: "sm" | "md";
}): JSX.Element | null {
  const u = leadUrgency(lead, now ?? new Date());
  if (u.tier === "none") return null;

  const isLast = u.tier === "last_chance";
  const main = (t: Theme): string =>
    isLast ? t.palette.error.main : t.palette.warning.main;

  return (
    <Box
      component="span"
      data-testid="lead-urgency-badge"
      data-urgency-tier={u.tier}
      title={
        isLast
          ? "Last chance — this lead's window closes within 3 days. Contact it now."
          : "Super hot — this lead's dated window closes within 2 weeks."
      }
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.375,
        borderRadius: 999,
        px: size === "sm" ? 0.75 : 1,
        py: 0.25,
        fontSize: size === "sm" ? 10 : 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        whiteSpace: "nowrap",
        color: main,
        bgcolor: (t: Theme) => alpha(main(t), 0.12),
        border: 1,
        borderColor: (t: Theme) => alpha(main(t), 0.45),
        ...(isLast
          ? {
              // A restrained opacity pulse (color-free, so it stays on-palette)
              // — disabled for users who prefer reduced motion.
              animation: "xopLastChancePulse 1.4s ease-in-out infinite",
              "@keyframes xopLastChancePulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.6 },
              },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }
          : {}),
      }}
    >
      <Box component="span" aria-hidden>
        {isLast ? "🚨" : "⏰"}
      </Box>{" "}
      {u.action}
    </Box>
  );
}
